import axios from 'axios';
import { toast } from 'react-toastify';

const api = axios.create({
    baseURL: process.env.REACT_APP_API_URL || 'http://localhost:3001',
    // [FASE 3] envia/recebe cookies HttpOnly de sessão (blb_access/blb_refresh)
    // em requisições cross-site. O backend responde Access-Control-Allow-Credentials.
    withCredentials: true,
});

/* ============================================================
   Rotas públicas — NÃO disparam refresh nem logout em 401
   ============================================================ */
const PUBLIC_PREFIXES = [
    '/api/public/',
    '/api/auth/',
    '/avaliar-chamado/',
    '/cotacao/',
    '/rastreio/',
];

function isPublicRoute(url = '') {
    return PUBLIC_PREFIXES.some(prefix => url.includes(prefix));
}

/* ============================================================
   CSRF (double-submit, cookie-to-header)
   ------------------------------------------------------------
   Cross-site: o front NÃO lê o cookie blb_csrf (domínios
   diferentes). O backend devolve o token no header de resposta
   X-CSRF-Token (exposto no CORS); guardamos em memória e ecoamos
   em requisições mutáveis. O backend compara o header com o
   cookie blb_csrf (que o browser envia automaticamente à API).
   ============================================================ */
let csrfToken = null;
const MUTATING = ['post', 'put', 'patch', 'delete'];

/* ============================================================
   Interceptador de REQUISIÇÕES
   ============================================================ */
api.interceptors.request.use(
    (config) => {
        // 1. Autenticação
        //    - Impersonação: sempre via header (sessionStorage), fluxo próprio.
        //    - [FASE 3] Sessão web normal: cookie HttpOnly (não há token no
        //      localStorage). O header abaixo só é setado para impersonação.
        //
        // [REDE DE SEGURANÇA] Muitos componentes legados ainda injetam
        // `Authorization: Bearer ${localStorage.getItem('token')}` manualmente no
        // config. Pós-migração esse token é null → viraria "Bearer null", e como
        // o backend é header-first isso QUEBRARIA a autenticação (ignoraria o
        // cookie válido). Então:
        //   (a) só montamos o header quando há um token REAL (impersonação);
        //   (b) se algum componente já colocou um Authorization inválido
        //       ("Bearer null"/"Bearer undefined"/vazio), REMOVEMOS — deixando o
        //       cookie HttpOnly assumir. Isso protege os componentes legados sem
        //       precisar tocar em cada um agora.
        const rawLegacy = localStorage.getItem('token');
        const legacyToken =
            rawLegacy && rawLegacy !== 'null' && rawLegacy !== 'undefined'
                ? rawLegacy
                : null;
        const impersonationToken = sessionStorage.getItem('impersonation_token');
        const token = impersonationToken || legacyToken;

        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        } else if (config.headers) {
            // Descarta Authorization inválido injetado por componente legado.
            const injected = config.headers.Authorization || config.headers.authorization;
            if (
                injected === undefined ||
                injected === null ||
                /^Bearer\s*(null|undefined)?\s*$/i.test(String(injected))
            ) {
                delete config.headers.Authorization;
                delete config.headers.authorization;
            }
        }

        // 2. Isolamento multi-tenant
        //    [FASE 3] O tenant ativo do admin vive no cookie/token (active_tenant_id),
        //    resolvido pelo tenantMiddleware. NÃO enviamos mais x-tenant-id a partir
        //    do localStorage: a chave selectedTenantId foi aposentada e enviá-la
        //    poderia forçar um tenant divergente do da sessão. Mantido apenas se um
        //    fluxo específico ainda depender de tenantId legado explícito.
        const tenantId = localStorage.getItem('tenantId');
        if (tenantId) {
            config.headers['x-tenant-id'] = tenantId;
        }

        // 3. [FASE 4] CSRF: ecoa o token (capturado das respostas) nas mutáveis.
        const method = (config.method || 'get').toLowerCase();
        if (csrfToken && MUTATING.includes(method)) {
            config.headers['X-CSRF-Token'] = csrfToken;
        }

        return config;
    },
    (error) => Promise.reject(error)
);

/* ============================================================
   Refresh de sessão — single-flight
   ------------------------------------------------------------
   Uma única chamada /api/auth/refresh é compartilhada por todas
   as requisições que tomaram 401 ao mesmo tempo.
   ============================================================ */
let refreshPromise = null;

function refreshSession() {
    if (!refreshPromise) {
        refreshPromise = api
            .post('/api/auth/refresh', {}, { _skipAuthHandling: true })
            .finally(() => { refreshPromise = null; });
    }
    return refreshPromise;
}

/* ============================================================
   Interceptador de RESPOSTAS
   ============================================================ */
api.interceptors.response.use(
    (response) => {
        // [FASE 4] captura o CSRF rotacionado (login/refresh/qualquer resposta).
        const t = response.headers?.['x-csrf-token'];
        if (t) csrfToken = t;
        return response;
    },

    async (error) => {
        const cfg        = error.config || {};
        const requestUrl = cfg.url || '';
        const status     = error.response?.status;

        // Chamadas internas (bootstrap/refresh) tratam o próprio erro.
        if (cfg._skipAuthHandling) {
            return Promise.reject(error);
        }

        // Rotas públicas nunca disparam refresh/logout.
        if (isPublicRoute(requestUrl)) {
            return Promise.reject(error);
        }

        const isTimeUpError =
            status === 403 &&
            error.response?.data?.error?.includes('horário de acesso terminou');

        // [TRIAL] Período de teste encerrado — backend responde 403 code=TRIAL_EXPIRED
        // em QUALQUER rota autenticada (login/ping/refresh/etc.). Trata igual ao
        // isTimeUpError: avisa, limpa a sessão local e manda pro /login (que exibe a
        // mensagem vinda do backend). NÃO tenta refresh — a sessão morreu de propósito.
        const isTrialExpired =
            status === 403 && error.response?.data?.code === 'TRIAL_EXPIRED';

        const isAuthError = status === 401;

        // [FASE 3] 401 → tenta renovar a sessão 1x via cookie e repete a request.
        // Impersonação não usa refresh (token de header, curto, próprio fluxo).
        // [TRIAL] não tenta refresh quando a sessão foi encerrada por trial.
        if (isAuthError && !isTrialExpired && !cfg._retry && !sessionStorage.getItem('impersonation_token')) {
            cfg._retry = true;
            try {
                await refreshSession();
                return api(cfg); // repete a request original com a sessão renovada
            } catch (_) {
                // refresh falhou → segue para o fluxo de logout abaixo
            }
        }

        if (isTrialExpired) {
            const msg = error.response.data?.error || 'Seu período de testes acabou. Acione o gestor da conta.';
            toast.warn(msg);
            clearSession();
            setTimeout(() => { window.location.href = '/login'; }, 3000);

        } else if (isTimeUpError) {
            const msg = error.response.data?.error || 'Seu horário de trabalho terminou.';
            toast.warn(msg);
            clearSession();
            setTimeout(() => { window.location.href = '/login'; }, 3000);

        } else if (isAuthError) {
            const msg = error.response?.data?.error || 'Sua sessão expirou. Faça login novamente.';
            toast.warn(msg);
            clearSession();
            setTimeout(() => { window.location.href = '/login'; }, 2500);
        }

        return Promise.reject(error);
    }
);

/* ============================================================
   Helper — limpeza completa de sessão (lado cliente)
   ------------------------------------------------------------
   Os cookies HttpOnly são limpos pelo backend no /logout; aqui
   removemos apenas o estado do lado do cliente.
   ============================================================ */
function clearSession() {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('tenantId');
    localStorage.removeItem('tenant_id');
    localStorage.removeItem('selectedTenantId');
    localStorage.removeItem('selectedTenantName');
    sessionStorage.removeItem('impersonation_token');
    sessionStorage.removeItem('impersonated_name');
    delete api.defaults.headers.common.Authorization;
    csrfToken = null;
}

export default api;